import { SparklesIcon } from "lucide-react";
import { memo } from "react";

import type {
  AiChatAction,
  AiSuggestedQuery,
} from "@/feature/ai-planner/types/ai-chat.types";
import { ChatMessageItem } from "./chat-message-item";
import type { ChatTurn } from "../types/chat-ui.types";

type ChatTurnItemProps = {
  turn: ChatTurn;
  onAction?: (action: AiChatAction) => void;
  onSuggestedQuery?: (query: AiSuggestedQuery) => void;
  onResponseStreamingComplete?: (message: ChatTurn["response"]) => void;
  showSuggestions?: boolean;
  actionsDisabled?: boolean;
  pendingSuggestedQueryId?: string | null;
  isPendingResponse?: boolean;
  isLast?: boolean;
  minHeight?: number;
};

export const ChatTurnItem = memo(function ChatTurnItem({
  turn,
  onAction,
  onSuggestedQuery,
  onResponseStreamingComplete,
  showSuggestions = true,
  actionsDisabled = false,
  pendingSuggestedQueryId,
  isPendingResponse = false,
  isLast = false,
  minHeight,
}: ChatTurnItemProps) {
  return (
    <article
      data-turn-id={turn.id}
      data-scroll-anchor={isLast}
      className="w-full min-w-0"
      style={isLast && minHeight ? { minHeight } : undefined}
    >
      <div className="min-w-0 space-y-5">
        <ChatMessageItem message={turn.request} />
        {turn.response ? (
          <ChatMessageItem
            message={turn.response}
            onAction={onAction}
            onSuggestedQuery={onSuggestedQuery}
            onStreamingComplete={onResponseStreamingComplete}
            showSuggestions={showSuggestions}
            actionsDisabled={actionsDisabled}
            pendingSuggestedQueryId={pendingSuggestedQueryId}
          />
        ) : isPendingResponse ? (
          <div className="flex min-w-0 items-start gap-3">
            <span className="mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-full border border-border/70 bg-muted/60 text-muted-foreground">
              <SparklesIcon className="size-3.5" />
            </span>
            <div className="flex min-w-0 items-center gap-1.5 pt-2">
              <span className="bg-foreground/65 inline-flex size-1.5 animate-bounce rounded-full [animation-delay:0ms]" />
              <span className="bg-foreground/65 inline-flex size-1.5 animate-bounce rounded-full [animation-delay:150ms]" />
              <span className="bg-foreground/65 inline-flex size-1.5 animate-bounce rounded-full [animation-delay:300ms]" />
            </div>
          </div>
        ) : null}
      </div>
    </article>
  );
});
